import { useState } from "react";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";
import { Label, Input, PrimaryButton, GhostButton } from "@/components/fields";

export default function RefundControl({ kind, item, onDone }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(item.deposit_amount ?? 0);
  const [busy, setBusy] = useState(false);

  const status = item.deposit_status;
  if (status === "refunded" || status === "partially_refunded") {
    return (
      <span className="text-xs text-ink/40 capitalize" data-testid={`refunded-label-${item.id}`}>
        {status.replace("_", " ")}
      </span>
    );
  }
  if (status !== "paid") return null;

  const refund = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/refunds", { kind, item_id: item.id, amount: Number(amount) });
      toast.success(`Refunded $${data.refunded}`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid={`refund-btn-${item.id}`}
        onClick={() => {
          setAmount(item.deposit_amount);
          setOpen(true);
        }}
        className="inline-flex items-center gap-1.5 rounded-full border border-destructive/30 text-destructive px-3 py-1.5 text-xs font-semibold hover:bg-destructive/10 transition-colors"
      >
        <RotateCcw size={12} /> Refund ${item.deposit_amount}
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Refund deposit" testid="refund-modal">
        <div className="space-y-4">
          <p className="text-sm text-ink/60 leading-relaxed">
            Issue a full or partial refund of the <strong>${item.deposit_amount}</strong> deposit paid by{" "}
            <strong>{item.user_name || item.name}</strong>. The guest is notified by email automatically.
          </p>
          <div>
            <Label>Amount (USD)</Label>
            <Input
              type="number"
              min="1"
              max={item.deposit_amount}
              step="0.01"
              data-testid="refund-amount-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <GhostButton data-testid="refund-full-btn" onClick={() => setAmount(item.deposit_amount)}>
              Full ${item.deposit_amount}
            </GhostButton>
            <GhostButton data-testid="refund-half-btn" onClick={() => setAmount(Math.round(item.deposit_amount * 50) / 100)}>
              50%
            </GhostButton>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <GhostButton data-testid="refund-cancel-btn" onClick={() => setOpen(false)}>
              Cancel
            </GhostButton>
            <PrimaryButton data-testid="refund-confirm-btn" onClick={refund} disabled={busy}>
              {busy ? "Refunding…" : `Refund $${amount}`}
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </>
  );
}
